/**
 * Dual Supabase Client Setup
 *
 * Two SDK clients:
 * - mainSupabase: Main project (auth, shared data, realtime)
 * - localSupabase: Local project (prototype-specific data)
 *
 * Two token functions:
 * - enhanceMainToken(): Get enhanced JWT for main project (with prototype claims)
 * - exchangeLocalToken(): Get JWT for local project access
 *
 * @see docs/DUAL-CLIENT-SETUP.md
 * @see docs/MULTI-TENANCY-AUTH.md
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';

// ============================================================================
// CONFIGURATION
// ============================================================================

// Main project (centralized auth + shared data)
const MAIN_SUPABASE_URL = import.meta.env.VITE_MAIN_SUPABASE_URL || '';
const MAIN_SUPABASE_ANON_KEY = import.meta.env.VITE_MAIN_SUPABASE_ANON_KEY || '';

// Local project (prototype-specific data)
const LOCAL_SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';
const LOCAL_SUPABASE_ANON_KEY =
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ||
  import.meta.env.VITE_SUPABASE_ANON_KEY ||
  '';

// CRM project (Client Hub Operational CRM — read-only source for Reservations report).
// Publishable values: safe to ship in the client bundle.
const CRM_SUPABASE_URL = 'https://wocbyonwdnhinjipovhp.supabase.co';
const CRM_SUPABASE_ANON_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6IndvY2J5b253ZG5oaW5qaXBvdmhwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI1NTQzOTYsImV4cCI6MjA4ODEzMDM5Nn0.AzA3o5ddPp7SZC7kW5RpOBLJXC5YHGL-2qOuIQOMrBY';

// Prototype API key is no longer bundled in the client. The local
// `request-enhanced-token` edge function holds it as a server-side secret
// and proxies the call to the main project's `enhance-token`.

// ============================================================================
// SDK CLIENTS
// ============================================================================

/**
 * Main project client - for auth and shared data.
 * This is the ONLY client that handles GoTrue authentication.
 */
export const mainSupabase: SupabaseClient = createClient(
  MAIN_SUPABASE_URL,
  MAIN_SUPABASE_ANON_KEY,
  {
    auth: {
      storage: localStorage,
      storageKey: 'main-auth-token',
      persistSession: true,
      autoRefreshToken: true,
    },
  }
);

/**
 * Local project client - anon operations only.
 * For authenticated operations, use getAuthenticatedLocalClient().
 */
export const localSupabase: SupabaseClient = createClient(
  LOCAL_SUPABASE_URL,
  LOCAL_SUPABASE_ANON_KEY,
  {
    auth: {
      // Do NOT persist a local-project GoTrue session. Dual-tenant users
      // authenticate via the main project; the local-side token is the
      // *exchanged* JWT held in _localAccessToken (re-issued each load).
      // Persisting here causes startup races where a stale local-only
      // session demotes a freshly-authenticated super_admin to authSource:'local',
      // wiping enhancedMainToken and breaking all Core reads.
      storageKey: 'local-auth-token',
      persistSession: false,
      autoRefreshToken: false,
    },
  }
);

// ============================================================================
// AUTHENTICATED LOCAL CLIENT (Header Injection Pattern)
// ============================================================================

let _localAccessToken: string | null = null;
let _mainAccessToken: string | null = null;
let _authenticatedLocalClient: SupabaseClient | null = null;
let _authenticatedLocalClientToken: string | null = null;
let _authenticatedMainClient: SupabaseClient | null = null;
let _authenticatedMainClientToken: string | null = null;

/** Decode `exp` (unix seconds) from a JWT, or null if undecodable. */
export function getJwtExp(token: string | null): number | null {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/** True when token is missing, undecodable, or within `marginSec` of `exp`. */
export function isJwtNearExpiry(token: string | null, marginSec = 120): boolean {
  const exp = getJwtExp(token);
  if (exp === null) return true;
  return exp - Math.floor(Date.now() / 1000) < marginSec;
}

/**
 * Coordinated refresh promise. Set by the auth hook via
 * `registerDownstreamRefresher`. The auth-aware fetch awaits this before
 * replaying a 401'd request so the retry uses a fresh Authorization header.
 */
let _downstreamRefresher: (() => Promise<void>) | null = null;
let _inFlightRefresh: Promise<void> | null = null;

export function registerDownstreamRefresher(fn: (() => Promise<void>) | null): void {
  _downstreamRefresher = fn;
}

export async function refreshDownstreamTokens(): Promise<void> {
  if (!_downstreamRefresher) return;
  if (!_inFlightRefresh) {
    _inFlightRefresh = _downstreamRefresher().finally(() => {
      _inFlightRefresh = null;
    });
  }
  return _inFlightRefresh;
}

/**
 * Ensure the cached CRM JWT is valid for at least ~60s before the caller
 * fires a request. Prevents the "wide date-range fetch after inactivity
 * returns empty" class of bugs by refreshing proactively rather than
 * relying only on the retry-on-401 path.
 */
export async function ensureCrmTokenFresh(marginSec = 60): Promise<string | null> {
  if (!_crmAccessToken || isJwtNearExpiry(_crmAccessToken, marginSec)) {
    try { await refreshDownstreamTokens(); } catch { /* surface via caller */ }
  }
  return _crmAccessToken;
}

/**
 * Same as {@link ensureCrmTokenFresh} but for the LOCAL project JWT (used by
 * the crm-read edge-function proxy path).
 */
export async function ensureLocalTokenFresh(marginSec = 60): Promise<string | null> {
  if (!_localAccessToken || isJwtNearExpiry(_localAccessToken, marginSec)) {
    try { await refreshDownstreamTokens(); } catch { /* surface via caller */ }
  }
  return _localAccessToken;
}

/**
 * Wrap fetch so that:
 *  - a downstream request returning 401/403 with a JWT-expired body triggers a
 *    coordinated re-exchange of the local/CRM JWTs, and
 *  - the offending request is replayed exactly once with the fresh
 *    Authorization header (guarded by `x-lovable-retry` to prevent loops).
 * Callers stop seeing sporadic 401 errors after periods of inactivity.
 */
function makeAuthAwareFetch(kind: 'local' | 'crm'): typeof fetch {
  return async (input, init) => {
    const res = await fetch(input as RequestInfo, init);
    if (res.status !== 401 && res.status !== 403) return res;

    // Only act on JWT-expiry-shaped failures. Genuine auth/permission errors
    // (e.g. RLS deny) MUST still surface to the caller.
    let looksLikeExpiry = false;
    try {
      const text = await res.clone().text();
      looksLikeExpiry = /JWT expired|invalid JWT|JWSError|PGRST301|PGRST303|token is expired|token has expired|invalid token/i.test(text);
    } catch { /* ignore */ }
    if (!looksLikeExpiry) {
      // Some gateways surface expiry only via the WWW-Authenticate header
      // (e.g. `Bearer error="invalid_token"`) with an empty/HTML body.
      const wwwAuth = res.headers.get('www-authenticate') || '';
      if (/invalid_token|expired/i.test(wwwAuth)) looksLikeExpiry = true;
    }
    if (!looksLikeExpiry) return res;

    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('auth:downstream-token-expired', { detail: { kind } }),
      );
    }

    // Guard against retry loops.
    const headers = new Headers(init?.headers as HeadersInit | undefined);
    if (headers.get('x-lovable-retry') === '1') return res;

    try {
      await refreshDownstreamTokens();
    } catch { return res; }

    const freshToken = kind === 'local' ? _localAccessToken : _crmAccessToken;
    if (!freshToken) return res;

    headers.set('Authorization', `Bearer ${freshToken}`);
    headers.set('x-lovable-retry', '1');
    return fetch(input as RequestInfo, { ...init, headers });
  };
}

export function setLocalAccessToken(token: string | null) {
  _localAccessToken = token;
  if (_authenticatedLocalClientToken !== token) {
    _authenticatedLocalClient = null;
    _authenticatedLocalClientToken = null;
  }
}

export function getLocalAccessToken(): string | null {
  return _localAccessToken;
}

export function setMainAccessToken(token: string | null) {
  _mainAccessToken = token;
  if (_authenticatedMainClientToken !== token) {
    _authenticatedMainClient = null;
    _authenticatedMainClientToken = null;
  }
}

export function getMainAccessToken(): string | null {
  return _mainAccessToken;
}

/**
 * Get authenticated Supabase client for main (Core) project.
 *
 * Uses the enhanced JWT (with tenant_id, prototype_id claims)
 * so that tenant-isolated RLS policies pass.
 *
 * Returns `null` when the enhanced token is not yet ready. Callers MUST
 * handle null and wait — silently falling back to the raw GoTrue session
 * masks RLS failures (rows return as empty/0) and was the root cause of
 * Core data appearing to disappear for super_admins after token enhancement
 * regressions.
 */
export function getAuthenticatedMainClient(): SupabaseClient | null {
  if (!_mainAccessToken) {
    return null;
  }

  if (_authenticatedMainClient && _authenticatedMainClientToken === _mainAccessToken) {
    return _authenticatedMainClient;
  }

  _authenticatedMainClient = createClient(MAIN_SUPABASE_URL, MAIN_SUPABASE_ANON_KEY, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
    },
    global: {
      headers: {
        Authorization: `Bearer ${_mainAccessToken}`,
      },
    },
  });

  _authenticatedMainClientToken = _mainAccessToken;
  return _authenticatedMainClient;
}

/**
 * Raw main client backed by the plain GoTrue session (no enhanced claims).
 *
 * Use ONLY for read-only count/diagnostic queries against Core tables whose
 * RLS predates the prototype/tenant claim system (e.g. the Guesty review
 * tables on Core, which return 0 rows under the enhanced JWT but full counts
 * under the raw GoTrue session).
 *
 * For any write path, or any RLS-isolated read, keep using
 * getAuthenticatedMainClient() — the strict enhanced-only contract is what
 * reservations and the sync edge function rely on.
 */
export function getRawMainClient(): SupabaseClient {
  return mainSupabase;
}

/**
 * Raw main GoTrue access token (no enhanced claims).
 *
 * Required for edge functions that read Core review/listing tables, whose
 * RLS returns [] under the enhanced JWT. The browser-side count path uses
 * `getRawMainClient()` for the same reason; this is the server-bound
 * equivalent — pass it as `body.main_token` to the sync edge functions.
 */
export async function getRawMainAccessToken(): Promise<string | null> {
  const { data: { session } } = await mainSupabase.auth.getSession();
  return session?.access_token ?? null;
}

/**
 * Get authenticated Supabase client for local project.
 *
 * CRITICAL: This is the ONLY correct way to use exchanged tokens.
 * Do NOT use setSession() — it only works with GoTrue-issued tokens.
 *
 * PostgREST validates JWT signature and extracts 'sub' claim → auth.uid()
 */
export function getAuthenticatedLocalClient(): SupabaseClient | null {
  if (!_localAccessToken) {
    return null;
  }

  if (_authenticatedLocalClient && _authenticatedLocalClientToken === _localAccessToken) {
    return _authenticatedLocalClient;
  }

  _authenticatedLocalClient = createClient(LOCAL_SUPABASE_URL, LOCAL_SUPABASE_ANON_KEY, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
    },
    global: {
      headers: {
        Authorization: `Bearer ${_localAccessToken}`,
      },
      fetch: makeAuthAwareFetch('local'),
    },
  });

  _authenticatedLocalClientToken = _localAccessToken;
  return _authenticatedLocalClient;
}

// ============================================================================
// TOKEN ENHANCEMENT (Main Project)
// ============================================================================

export interface EnhanceTokenResult {
  access_token: string;
  token_type: string;
  expires_in: number;
  expires_at: number;
  prototype: {
    id: string;
    name: string;
    is_internal: boolean;
  };
}

/**
 * Enhance main token with prototype claims.
 * Required for main project RLS to know which prototype is accessing data.
 */
export async function enhanceMainToken(mainToken: string): Promise<EnhanceTokenResult> {
  // Call our LOCAL edge function which holds PROTOTYPE_API_KEY server-side
  // and proxies to the main project's enhance-token. This keeps the prototype
  // API key out of the browser bundle.
  const response = await fetch(`${LOCAL_SUPABASE_URL}/functions/v1/request-enhanced-token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${LOCAL_SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({ mainToken }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Enhancement failed' }));
    throw new Error(error.error || `Token enhancement failed: ${response.status}`);
  }

  return response.json();
}

// ============================================================================
// LOCAL JWT SESSION CACHE
// ============================================================================
//
// The exchange-token edge function call is the single biggest hop on cold load
// (~250–600ms). The returned JWT is stable until its `exp`, so we cache it in
// sessionStorage keyed by the main user id. sessionStorage (not localStorage)
// because the cache should die with the tab — matches the existing rule that
// the local SDK client never persists a GoTrue session.
//
// Safety: if the cached token is within 120s of expiry, or the user id changes,
// or decoding fails, we fall through to a live exchange. RLS surface is
// identical to the un-cached path — same JWT, same `sub`, same signature.

const LOCAL_JWT_CACHE_KEY = 'local-jwt-cache:v1';
const LOCAL_JWT_SAFETY_MARGIN_SEC = 120;

interface CachedLocalToken {
  userId: string;
  token: string;
  exp: number; // unix seconds
}

export function getCachedLocalToken(userId: string): { token: string; exp: number } | null {
  try {
    const raw = sessionStorage.getItem(LOCAL_JWT_CACHE_KEY);
    if (!raw) return null;
    const parsed: CachedLocalToken = JSON.parse(raw);
    if (parsed.userId !== userId) return null;
    const nowSec = Math.floor(Date.now() / 1000);
    if (parsed.exp - nowSec < LOCAL_JWT_SAFETY_MARGIN_SEC) return null;
    return { token: parsed.token, exp: parsed.exp };
  } catch {
    return null;
  }
}

export function setCachedLocalToken(userId: string, token: string, exp: number): void {
  try {
    const payload: CachedLocalToken = { userId, token, exp };
    sessionStorage.setItem(LOCAL_JWT_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage may be unavailable (privacy mode); silently skip.
  }
}

export function clearCachedLocalToken(): void {
  try {
    sessionStorage.removeItem(LOCAL_JWT_CACHE_KEY);
  } catch {
    // ignore
  }
}

// ============================================================================
// TOKEN EXCHANGE (Local Project)
// ============================================================================

export interface ExchangeTokenResult {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  expires_at: number;
}

/**
 * Exchange main token for local project JWT.
 * The local project verifies the main token signature and issues a new JWT.
 */
export async function exchangeLocalToken(mainToken: string): Promise<ExchangeTokenResult> {
  const response = await fetch(`${LOCAL_SUPABASE_URL}/functions/v1/exchange-token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${LOCAL_SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({ mainToken }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Exchange failed' }));
    throw new Error(error.error || `Token exchange failed: ${response.status}`);
  }

  return response.json();
}

// ============================================================================
// TOKEN SETTERS FOR REALTIME
// ============================================================================

/**
 * Set token on main client for Realtime subscriptions
 */
export function setMainToken(token: string) {
  mainSupabase.realtime.setAuth(token);
}

/**
 * Set token on local client for Realtime subscriptions
 */
export function setLocalToken(token: string) {
  localSupabase.realtime.setAuth(token);
}

// ============================================================================
// CONFIGURATION CHECK
// ============================================================================

export function isSupabaseConfigured(): boolean {
  return !!(MAIN_SUPABASE_URL && MAIN_SUPABASE_ANON_KEY);
}

export function isLocalConfigured(): boolean {
  return !!(LOCAL_SUPABASE_URL && LOCAL_SUPABASE_ANON_KEY);
}

// ============================================================================
// CRM PROJECT CLIENT (Client Hub Operational CRM — read-only)
// ============================================================================
//
// The Reservations report reads from CRM's local DB (`pricelabs_reservations`,
// `property`). CRM exposes its own `exchange-token` edge function that mints a
// CRM-local JWT from a Main JWT (same pattern as our local exchange).

let _crmAccessToken: string | null = null;
let _authenticatedCrmClient: SupabaseClient | null = null;
let _authenticatedCrmClientToken: string | null = null;

export function setCrmAccessToken(token: string | null) {
  _crmAccessToken = token;
  if (_authenticatedCrmClientToken !== token) {
    _authenticatedCrmClient = null;
    _authenticatedCrmClientToken = null;
  }
}

export function getCrmAccessToken(): string | null {
  return _crmAccessToken;
}

export function getAuthenticatedCrmClient(): SupabaseClient | null {
  if (!_crmAccessToken) return null;

  if (_authenticatedCrmClient && _authenticatedCrmClientToken === _crmAccessToken) {
    return _authenticatedCrmClient;
  }

  _authenticatedCrmClient = createClient(CRM_SUPABASE_URL, CRM_SUPABASE_ANON_KEY, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
    },
    global: {
      headers: {
        Authorization: `Bearer ${_crmAccessToken}`,
      },
      fetch: makeAuthAwareFetch('crm'),
    },
  });

  _authenticatedCrmClientToken = _crmAccessToken;
  return _authenticatedCrmClient;
}

// CRM JWT cache — same shape/contract as the local cache.
const CRM_JWT_CACHE_KEY = 'crm-jwt-cache:v1';
const CRM_JWT_SAFETY_MARGIN_SEC = 120;

interface CachedCrmToken {
  userId: string;
  token: string;
  exp: number;
}

export function getCachedCrmToken(userId: string): { token: string; exp: number } | null {
  try {
    const raw = sessionStorage.getItem(CRM_JWT_CACHE_KEY);
    if (!raw) return null;
    const parsed: CachedCrmToken = JSON.parse(raw);
    if (parsed.userId !== userId) return null;
    const nowSec = Math.floor(Date.now() / 1000);
    if (parsed.exp - nowSec < CRM_JWT_SAFETY_MARGIN_SEC) return null;
    return { token: parsed.token, exp: parsed.exp };
  } catch {
    return null;
  }
}

export function setCachedCrmToken(userId: string, token: string, exp: number): void {
  try {
    const payload: CachedCrmToken = { userId, token, exp };
    sessionStorage.setItem(CRM_JWT_CACHE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

export function clearCachedCrmToken(): void {
  try {
    sessionStorage.removeItem(CRM_JWT_CACHE_KEY);
  } catch {
    /* ignore */
  }
}

export interface ExchangeCrmTokenResult {
  access_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
  expires_at: number;
}

/**
 * Exchange a Main JWT for a CRM-local JWT by calling CRM's `exchange-token`
 * edge function directly. No edge function is deployed in this project for
 * this — we reuse CRM's existing endpoint.
 */
export async function exchangeCrmToken(mainToken: string): Promise<ExchangeCrmTokenResult> {
  const response = await fetch(`${CRM_SUPABASE_URL}/functions/v1/exchange-token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${CRM_SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({ mainToken }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'CRM exchange failed' }));
    throw new Error(error.error || `CRM token exchange failed: ${response.status}`);
  }

  return response.json();
  return response.json();
}

/**
 * Exchange a LOCAL JWT for a CRM-local JWT by calling our LOCAL
 * `mint-crm-token-local` edge function. Used for users that only exist on
 * the local project (no main account) so the Owner Dashboard's CRM-bound
 * filters/data still populate.
 */
export async function exchangeCrmTokenFromLocal(localToken: string): Promise<ExchangeCrmTokenResult> {
  const response = await fetch(`${LOCAL_SUPABASE_URL}/functions/v1/mint-crm-token-local`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localToken}`,
      'apikey': LOCAL_SUPABASE_ANON_KEY,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'CRM exchange (local) failed' }));
    throw new Error(error.error || `CRM token exchange (local) failed: ${response.status}`);
  }

  return response.json();
}

